import format from 'string-template';

import { storagePaths, pathPlaceholder } from '../../../../../utils/firebasePaths';
import { uploadMessageAttachments, getProcessedAttachmentData } from '../../../../../utils/attachments';

const MAX_FILE_UPLOAD_SIZE = 100000000; // 100MB

const ErrorTypes = {
  FILE_SIZE: 'FILE_SIZE',
  AUDIO_FILE: 'AUDIO_FILE',
};

/**
 * Process the attachments for the announcement.
 *
 * @param {Object[]} fileAttachments - The files to process.
 * @param {string} coachId - The coach id.
 * @param {string} loggedInUserId - The logged in user's id.
 * @param {Function} uploadAttachments - The function to upload the attachments.
 * @param {Function} deleteAttachments - The function to delete the attachments.
 * @returns {Promise<{attachments: Object[], existingAttachments: Object[], error: boolean}>}
 */
const processAttachments = async ({
  fileAttachments,
  coachId,
  loggedInUserId,
  uploadAttachments,
  deleteAttachments,
}) => {
  const attachments = [];

  // Separate existing attachments from new ones
  const existingAttachments = [];
  const newAttachments = [];

  fileAttachments.forEach((file) => {
    // If the file has a url, it is an existing attachment. We just need to get the processed attachment data.
    if (file.url) {
      attachments.push(getProcessedAttachmentData({
        fileName: file.name,
        pathRef: file.url,
        contentType: file.type,
        fileSize: file.size,
      }));
      // If an error occurs while uploading the new attachments, we need to restore the existing attachments.
      existingAttachments.push(file);
    } else {
      // If not, it is a new attachment. We need to upload it.
      newAttachments.push(file);
    }
  });

  if (newAttachments.length) {
    const storagePath = format(storagePaths.COACH_ANNOUNCEMENTS_ATTACHMENTS, {
      [pathPlaceholder.COACH_ID]: coachId,
    });

    const { storageTaskFailed, attachmentsData } = await uploadMessageAttachments({
      fileAttachments: newAttachments,
      loggedInUserId,
      uploadAttachments,
      deleteAttachments,
      storagePath,
    });

    if (storageTaskFailed) {
      return { error: true, attachments: existingAttachments };
    }
    attachments.push(...attachmentsData);
  }
  return { attachments, error: false };
};

const onFilesSelected = (event) => {
  const { files } = event.target;
  if (files.length) {
    const filesArray = [...files];
    const validFileSize = filesArray.every((file) => file.size < MAX_FILE_UPLOAD_SIZE);
    const hasAudioFile = filesArray.some((file) => file.type.startsWith('audio/'));

    if (!validFileSize) {
      return { error: ErrorTypes.FILE_SIZE, files: [] };
    }

    if (hasAudioFile) {
      return { error: ErrorTypes.AUDIO_FILE, files: [] };
    }

    return { error: null, files: filesArray };
  }
  return { error: null, files: [] };
};

export {
  MAX_FILE_UPLOAD_SIZE,
  ErrorTypes,
  processAttachments,
  onFilesSelected,
};
